import { useMutation, useQuery } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { Route, Routes as RouterRoutes, useParams } from "react-router-dom";

import AuthRoute from "../../../../components/AuthRoute";
import { createModuleErrorBoundary } from "../../../../components/ErrorBoundary";
import {
  FormSaveStatus,
  NavBar,
  Spinner,
} from "../../../../components/ui";
import { UPDATE_INVOICE as updateInvoiceMutation } from "../../../../graphql/mutation/Invoice";
import { FETCH_INVOICE as fetchInvoice } from "../../../../graphql/query/Invoice";
import useAutosaveFormik from "../../../../hooks/useAutosaveFormik";
import { getChangedFields, Portal } from "../../../../util";
import routes from "./routes";

const Body = props => {
  const { loading, ...remainingProps } = props;

  return loading ? (
    <div className="h-100% flex w-full items-center justify-center">
      <Spinner />
    </div>
  ) : (
    <Sentry.ErrorBoundary
      fallback={createModuleErrorBoundary("invoice-wizard")}
      beforeCapture={scope => {
        scope.setTag("module", "invoice-wizard");
      }}
    >
      <RouterRoutes>
        {routes
          ?.filter?.(item => item?.element)
          ?.map((item, index) => {
            return (
              <Route
                key={index}
                path={item?.href}
                element={
                  <AuthRoute permissions={item.permissions}>
                    <item.element {...remainingProps} />
                  </AuthRoute>
                }
              />
            );
          })}
      </RouterRoutes>
    </Sentry.ErrorBoundary>
  );
};

const InvoiceForm = props => {
  const { id, invoice } = props;
  const [updateInvoice] = useMutation(updateInvoiceMutation(), {
    refetchQueries: ["FetchInvoice"],
  });

  const formik = useAutosaveFormik({
    initialValues: invoice,
    autosaveDelay: 2000,
    savedStateDuration: 2000,
    onSave: async (values, savedValues) => {
      const changedFields = getChangedFields(values, savedValues);

      if (Object.keys(changedFields).length > 0) {
        const { data } = await updateInvoice({
          variables: {
            input: {
              id: invoice?.id,
              ...changedFields,
            },
          },
        });

        if (!data?.updateInvoice?.success) {
          throw new Error(
            data?.updateInvoice?.error || "The invoice could not be saved",
          );
        }
      }
    },
  });

  const bodyProps = {
    ...props,
    invoice,
    formik,
    invoiceId: id,
  };

  return (
    <div className="relative flex min-h-full">
      <Portal id="form-save-status">
        <FormSaveStatus state={formik.autosave.formState} />
      </Portal>
      <Portal id="sidebar">
        <NavBar
          navigation={routes?.filter?.(item => item?.icon)}
          open={props?.open}
        />
      </Portal>
      <div className="flex min-h-full min-w-0 flex-1 flex-col overflow-x-auto px-4 py-4 sm:px-6 lg:px-8 lg:py-8">
        <Body {...bodyProps} />
      </div>
    </div>
  );
};

const Wizard = props => {
  const { id } = useParams();

  const { data: { invoice } = {}, loading } = useQuery(
    fetchInvoice({
      bank: {},
      contact: {},
      company: {
        images: {},
        invoiceCode: true,
        crossInvoiceCompanyIds: true,
      },
      country: {},
      currency: {},
      invoiceSalesPersons: { salesUser: {}, invoiceSalesPersonType: {} },
      invoiceTotals: {},
      paymentPlan: true,
      quantity: {},
      refundableReceiptAmount: true,
      items: true,
    }),
    { variables: { id: +id } },
  );

  if (loading || !invoice) {
    return (
      <div className="relative flex min-h-full">
        <Portal id="sidebar">
          <NavBar
            navigation={routes?.filter?.(item => item?.icon)}
            open={props?.open}
          />
        </Portal>
        <div className="flex min-h-full min-w-0 flex-1 flex-col overflow-x-auto px-4 py-4 sm:px-6 lg:px-8 lg:py-8">
          <Body loading={true} />
        </div>
      </div>
    );
  }

  return <InvoiceForm key={invoice.id} {...props} id={+id} invoice={invoice} />;
};

export default Wizard;
